import { useContext, useEffect, useRef } from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";

/** Returns the warning to show while leaving would lose work, else null. */
type LeaveBlocker = () => string | null;

const blockers = new Set<{ current: LeaveBlocker }>();

// Set when the user has just agreed to leave, so the navigation that follows
// in the same tick (a caller's confirmLeave(), then its navigate()) isn't
// asked about a second time.
let leaveApproved = false;

const blockingMessage = (): string | null => {
  for (const blocker of Array.from(blockers)) {
    const message = blocker.current();
    if (message) return message;
  }
  return null;
};

/**
 * Asks the user to confirm leaving while a mounted useLeaveGuard is
 * blocking, and returns whether they agreed (true when nothing blocks).
 *
 * Navigation through the router is already guarded. Call this first from an
 * action that changes app state before it navigates (switching account,
 * signing out), so a "stay" leaves that state untouched too.
 */
export function confirmLeave(): boolean {
  if (leaveApproved) return true;
  const message = blockingMessage();
  if (!message) return true;
  if (!window.confirm(message)) return false;
  leaveApproved = true;
  queueMicrotask(() => {
    leaveApproved = false;
  });
  return true;
}

const historyIndex = (): number | null => {
  const idx = (window.history.state as { idx?: unknown } | null)?.idx;
  return typeof idx === "number" ? idx : null;
};

// The router's index of the entry the app is showing, kept while a guard is
// mounted, so a cancelled back/forward step can be undone.
let currentIndex: number | null = null;
let restoring = false;

// A back or forward step the user cancels must never reach the router, or it
// renders the other page and the upload is unmounted. Listeners on the same
// target run in the order they were added (whatever their capture flag), so
// this one is added when the module loads, before <BrowserRouter> mounts and
// adds its own; App.tsx imports this module for that reason.
const onPopState = (event: PopStateEvent) => {
  const nextIndex = historyIndex();
  if (restoring) {
    restoring = false;
    event.stopImmediatePropagation();
    return;
  }
  if (nextIndex === null || currentIndex === null || confirmLeave()) {
    currentIndex = nextIndex;
    return;
  }
  event.stopImmediatePropagation();
  restoring = true;
  window.history.go(currentIndex - nextIndex);
};
if (typeof window !== "undefined") {
  window.addEventListener("popstate", onPopState);
}

type HistoryNavigator = {
  push: (...args: unknown[]) => void;
  replace: (...args: unknown[]) => void;
};

/**
 * Warns before the user leaves the page while `getMessage` returns a message:
 * closing or reloading the tab (the browser's own prompt; it ignores the
 * text), following an in-app link or navigate(), and the browser's back and
 * forward buttons. Return null to let them leave freely.
 *
 * The app uses <BrowserRouter>, which has no useBlocker, so this wraps the
 * router's navigator and stops popstate before the router sees it.
 * Always reads the latest `getMessage`.
 */
export function useLeaveGuard(getMessage: LeaveBlocker): void {
  const blockerRef = useRef(getMessage);
  blockerRef.current = getMessage;
  // Null outside a router (a component rendered on its own in a test).
  const navigator = useContext(UNSAFE_NavigationContext)?.navigator as
    | HistoryNavigator
    | undefined;

  useEffect(() => {
    blockers.add(blockerRef);
    currentIndex = historyIndex();

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!blockerRef.current()) return;
      event.preventDefault();
      // Chrome < 119 only prompts when returnValue is set.
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    const unwrap = navigator ? wrapNavigator(navigator) : undefined;
    return () => {
      blockers.delete(blockerRef);
      window.removeEventListener("beforeunload", onBeforeUnload);
      unwrap?.();
    };
  }, [navigator]);
}

function wrapNavigator(navigator: HistoryNavigator): () => void {
  const { push, replace } = navigator;
  const guarded =
    (navigate: HistoryNavigator["push"]) =>
    (...args: unknown[]) => {
      if (!confirmLeave()) return;
      navigate.apply(navigator, args);
      currentIndex = historyIndex();
    };
  navigator.push = guarded(push);
  navigator.replace = guarded(replace);
  return () => {
    navigator.push = push;
    navigator.replace = replace;
  };
}
